import { Column } from '@ant-design/charts';
import { BarChartOutlined } from '@ant-design/icons';
import { Card, Empty } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { TurnCheckinStatusByDayData } from '../types';
import { CHECKIN_STATUS_COLORS, CheckinStatusKey } from '../utils';

const BASE =
  'scenes.kit.dashboard_alternative_mode.components.dashboard_stacked_bar_chart';

// Orden del stack: base → cima (on_time abajo, pending arriba visualmente)
const STACK_ORDER: CheckinStatusKey[] = [
  'on_time',
  'late',
  'absent',
  'pending',
];

interface DashboardStackedBarChartProps {
  data?: TurnCheckinStatusByDayData;
  loading?: boolean;
}

function DashboardStackedBarChart({
  data,
  loading,
}: DashboardStackedBarChartProps) {
  const { t, i18n } = useTranslation();

  const isEmpty = !data || Object.keys(data).length === 0;

  const statusLabels: Record<CheckinStatusKey, string> = {
    on_time: t(`${BASE}.series.on_time`),
    late: t(`${BASE}.series.late`),
    absent: t(`${BASE}.series.absent`),
    pending: t(`${BASE}.series.pending`),
  };

  const chartData: {
    day: string;
    value: number;
    count: number;
    category: string;
  }[] = [];

  if (!isEmpty) {
    Object.entries(data).forEach(([dateStr, row]) => {
      const d = dayjs(dateStr);
      const label =
        i18n.language === 'es' ? d.format('DD/MM') : d.format('MM/DD');

      const valuesByKey: Record<CheckinStatusKey, number> = {
        on_time: row.onTime,
        late: row.late,
        absent: row.absent,
        pending: row.pending,
      };

      STACK_ORDER.forEach((key) => {
        chartData.push({
          day: label,
          value: valuesByKey[key],
          count: valuesByKey[key],
          category: statusLabels[key],
        });
      });
    });
  }

  const hasData = chartData.some((d) => d.value > 0);

  const skeletonHeights = [60, 80, 40, 90, 55, 70, 45];

  const config = {
    data: chartData,
    xField: 'day',
    yField: 'value',
    colorField: 'category',
    stack: true,
    scale: {
      color: {
        domain: STACK_ORDER.map((k) => statusLabels[k]),
        range: STACK_ORDER.map((k) => CHECKIN_STATUS_COLORS[k].bar),
      },
      y: { nice: true },
    },
    axis: {
      y: {
        title: t(`${BASE}.axis.total`),
      },
    },
    style: { maxWidth: 40 },
    tooltip: {
      title: (datum: any) => datum.day,
      items: [
        {
          field: 'count',
          name: (datum: any) => datum.category,
          color: (datum: any) => {
            const key = STACK_ORDER.find(
              (k) => statusLabels[k] === datum.category
            );
            return key ? CHECKIN_STATUS_COLORS[key].bar : '#ccc';
          },
          valueFormatter: (val: number) => String(val),
        },
      ],
    },
    legend: false,
  };

  const cardTitle = t(`${BASE}.title`);

  const legendOrder: CheckinStatusKey[] = [
    'on_time',
    'late',
    'absent',
    'pending',
  ];

  return (
    <Card title={cardTitle} styles={{ body: { paddingTop: 12 } }}>
      <div style={{ marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: '#8c8c8c' }}>
          {t(`${BASE}.subtitle`)}
        </span>
      </div>

      {loading ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            height: 300,
            gap: 8,
            padding: '0 4px',
          }}
        >
          {skeletonHeights.map((h, idx) => (
            <div
              key={idx}
              className="kit-dashboard-alt__chart-skeleton-bar"
              style={{ height: h }}
            />
          ))}
        </div>
      ) : isEmpty || !hasData ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 300,
          }}
        >
          <Empty
            description={
              <span style={{ color: '#d9d9d9' }}>{t(`${BASE}.empty`)}</span>
            }
            image={
              <BarChartOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
            }
            imageStyle={{ height: 48, marginBottom: 4 }}
          />
        </div>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 20,
              marginBottom: 8,
              flexWrap: 'wrap',
            }}
          >
            {legendOrder.map((key) => (
              <div
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  color: '#595959',
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    width: 12,
                    height: 12,
                    borderRadius: 2,
                    background: CHECKIN_STATUS_COLORS[key].bar,
                    flexShrink: 0,
                  }}
                />
                {statusLabels[key]}
              </div>
            ))}
          </div>
          <Column {...(config as any)} height={280} />
        </>
      )}
    </Card>
  );
}

export default DashboardStackedBarChart;
